import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { tokenStore } from '../../shared/api/tokens';
import { Button } from '../../shared/ui/Button';
import { PasswordField } from '../../shared/ui/PasswordField';
import { resetPassword } from './api';
import { AuthShell } from './AuthShell';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState('');

  const mutation = useMutation({
    mutationFn: () => resetPassword(token, password),
    // Сервер отозвал все сессии пользователя — локальные токены тоже больше не нужны.
    onSuccess: () => tokenStore.clear(),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setFieldError('Минимум 8 символов');
      return;
    }
    if (password !== confirm) {
      setFieldError('Пароли не совпадают');
      return;
    }
    setFieldError('');
    mutation.mutate();
  };

  if (!token) {
    return (
      <AuthShell subtitle="Смена пароля">
        <p className="text-sm text-danger">
          В ссылке нет кода восстановления. Откройте ссылку из письма целиком или запросите новую.
        </p>
        <p className="mt-4 text-center text-sm">
          <Link to="/forgot-password" className="font-medium text-accent hover:text-accent-600">
            Запросить ссылку ещё раз
          </Link>
        </p>
      </AuthShell>
    );
  }

  if (mutation.isSuccess) {
    return (
      <AuthShell subtitle="Смена пароля">
        <p className="text-sm text-ink">Пароль изменён. Войдите с новым паролем.</p>
        <Link
          to="/login"
          replace
          className="mt-4 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          Войти
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell subtitle="Смена пароля">
      <form onSubmit={submit} className="space-y-4">
        <PasswordField
          label="Новый пароль"
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <PasswordField
          label="Повторите пароль"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={fieldError}
        />
        {mutation.isError && (
          <p className="text-sm text-danger">
            {apiErrorMessage(mutation.error, 'Не удалось сменить пароль')}
          </p>
        )}
        <Button type="submit" disabled={mutation.isPending} className="w-full">
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить пароль'}
        </Button>
      </form>
      {mutation.isError && (
        <p className="mt-4 text-center text-sm">
          <Link to="/forgot-password" className="font-medium text-accent hover:text-accent-600">
            Запросить новую ссылку
          </Link>
        </p>
      )}
    </AuthShell>
  );
}
